import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { BookOpen, Printer, Save, ArrowLeft } from "lucide-react";

const NIVELES_LETRA = ["NAE", "AE", "AS", "ADS"];
const NIVEL_LABEL = {
  NAE: "NAE · Nivel esperado alto",
  AE: "AE · En vías",
  AS: "AS · Satisfactorio",
  ADS: "ADS · Alcanzó y superó",
};
const CAMPOS = [
  { key: "lenguajes", label: "Lenguajes" },
  { key: "saberes", label: "Saberes y Pensamiento Científico" },
  { key: "etica", label: "Ética, Naturaleza y Sociedades" },
  { key: "humano", label: "De lo Humano y lo Comunitario" },
];

export default function Boletas() {
  const { grupoId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditEscala = user?.role === "superadmin" || user?.role === "director";
  const [grupo, setGrupo] = useState(null);
  const [plantel, setPlantel] = useState(null);
  const [alumnos, setAlumnos] = useState([]);
  const [trimestre, setTrimestre] = useState(1);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [escala, setEscala] = useState("LETRAS"); // LETRAS | NUMERICA

  const ciclo = grupo?.ciclo || "";

  const load = async () => {
    setLoading(true);
    try {
      const [g, al] = await Promise.all([
        api.get(`/grupos/${grupoId}`),
        api.get(`/alumnos?grupo_id=${grupoId}`),
      ]);
      setGrupo(g.data);
      setAlumnos(al.data);
      const p = (await api.get(`/planteles`)).data.find((x) => x.id === g.data.plantel_id);
      setPlantel(p);
      setEscala((p?.escala_boletas === "NUMERICA" ? "NUMERICA" : "LETRAS"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [grupoId]);

  useEffect(() => {
    if (!ciclo || !grupoId) return;
    (async () => {
      try {
        const { data } = await api.get(`/calificaciones?grupo_id=${grupoId}&ciclo=${ciclo}&trimestre=${trimestre}`);
        const map = {};
        (data || []).forEach((c) => {
          map[c.alumno_id] = {
            asistencias: c.asistencias || 0,
            lenguajes: c.lenguajes || "",
            saberes: c.saberes || "",
            etica: c.etica || "",
            humano: c.humano || "",
            observaciones: c.observaciones || "",
          };
        });
        setRows(map);
        setDirty(false);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [grupoId, ciclo, trimestre]);

  const changeEscala = async (v) => {
    if (!plantel) return;
    if (!canEditEscala) return;
    if (v === escala) return;
    if (dirty && !window.confirm("Tienes cambios sin guardar. ¿Cambiar escala y descartar cambios?")) return;
    setEscala(v);
    try {
      await api.put(`/planteles/${plantel.id}/escala-boletas`, { escala_boletas: v });
      toast.success(`Escala guardada: ${v === "NUMERICA" ? "Numérica (5-10)" : "Letras (NEM)"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setCell = (alumnoId, field, value) => {
    setRows((prev) => ({
      ...prev,
      [alumnoId]: { ...(prev[alumnoId] || {}), [field]: value },
    }));
    setDirty(true);
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const items = alumnos.map((a) => ({
        alumno_id: a.id,
        asistencias: parseInt(rows[a.id]?.asistencias || 0, 10) || 0,
        lenguajes: rows[a.id]?.lenguajes || "",
        saberes: rows[a.id]?.saberes || "",
        etica: rows[a.id]?.etica || "",
        humano: rows[a.id]?.humano || "",
        observaciones: rows[a.id]?.observaciones || "",
      }));
      await api.put(`/calificaciones/bulk`, { grupo_id: grupoId, ciclo, trimestre, items });
      toast.success(`Trimestre ${trimestre} guardado`);
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const imprimirGrupo = () => window.open(`/grupos/${grupoId}/boletas/print?trimestre=${trimestre}`, "_blank");
  const imprimirTodosIndividuales = () => window.open(`/grupos/${grupoId}/boletas/print?todos=1`, "_blank");
  const imprimirAlumno = (aid) => window.open(`/grupos/${grupoId}/boletas/print?alumno=${aid}`, "_blank");

  const nombreCompleto = (a) => [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" ").toUpperCase();
  const sortedAlumnos = useMemo(
    () => [...alumnos].sort((a, b) => nombreCompleto(a).localeCompare(nombreCompleto(b), "es")),
    [alumnos]
  );

  return (
    <AppLayout>
      <div className="max-w-7xl">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs text-[#545454] hover:text-[#10312B]" data-testid="btn-back-boletas">
            <ArrowLeft className="w-3.5 h-3.5" /> Regresar
          </button>
        </div>
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Boletas · Nueva Escuela Mexicana</div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {grupo ? `${grupo.grado}° "${grupo.letra}" · Ciclo ${grupo.ciclo}` : "Boletas"}
            </h1>
            <p className="text-sm text-[#545454] mt-1">Captura por trimestre. La escala queda guardada para toda la escuela.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <label className="text-[10px] text-[#6B7280] uppercase tracking-wider">Escala del plantel</label>
              {canEditEscala ? (
                <Select value={escala} onValueChange={changeEscala}>
                  <SelectTrigger data-testid="escala-select" className="h-8 text-xs w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LETRAS">Letras · NAE / AE / AS / ADS</SelectItem>
                    <SelectItem value="NUMERICA">Numérica · 5 a 10</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div data-testid="escala-readonly" className="h-8 text-xs w-44 px-3 flex items-center rounded border border-[#E5E7EB] bg-[#F9FAFB] text-[#374151]" title="Solo Director/Superadmin pueden cambiar la escala">
                  {escala === "NUMERICA" ? "Numérica · 5 a 10" : "Letras · NAE / AE / AS / ADS"}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3].map((t) => (
                <button
                  key={t}
                  data-testid={`tab-tri-${t}`}
                  onClick={() => setTrimestre(t)}
                  className={`px-3 py-1.5 text-xs rounded border transition-colors ${trimestre === t ? "bg-[#235B4E] text-white border-[#235B4E]" : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#235B4E]"}`}
                >Trimestre {["I", "II", "III"][t - 1]}</button>
              ))}
            </div>
            <Button data-testid="btn-save-boletas" onClick={guardar} disabled={saving || !dirty} className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Guardando…" : dirty ? "Guardar cambios" : "Sin cambios"}
            </Button>
            <Button data-testid="btn-print-grupo" onClick={imprimirGrupo} variant="outline" className="border-[#235B4E] text-[#235B4E]">
              <Printer className="w-4 h-4 mr-1" /> PDF grupo
            </Button>
            <Button data-testid="btn-print-todos" onClick={imprimirTodosIndividuales} variant="outline" className="border-[#9F2241] text-[#9F2241] hover:bg-[#9F2241] hover:text-white">
              <Printer className="w-4 h-4 mr-1" /> PDF todos (individuales)
            </Button>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#F9FAFB] text-[#374151]">
                  <th className="p-2 text-left w-10">#</th>
                  <th className="p-2 text-left">Alumno</th>
                  <th className="p-2 w-24 text-center">Asistencias</th>
                  {CAMPOS.map((c) => (<th key={c.key} className="p-2 w-36 text-center">{c.label}</th>))}
                  <th className="p-2 w-56">Observaciones</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="p-10 text-center text-[#9CA3AF]">Cargando…</td></tr>
                ) : sortedAlumnos.length === 0 ? (
                  <tr><td colSpan={8} className="p-10 text-center text-[#9CA3AF]">
                    <BookOpen className="w-6 h-6 mx-auto mb-2" />Sin alumnos en este grupo
                  </td></tr>
                ) : sortedAlumnos.map((a, i) => {
                  const r = rows[a.id] || {};
                  return (
                    <tr key={a.id} className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB]/50">
                      <td className="p-2 text-[#9CA3AF]">{i + 1}</td>
                      <td className="p-2 font-medium text-[#10312B]">{nombreCompleto(a)}</td>
                      <td className="p-1.5">
                        <Input data-testid={`b-asis-${a.id}`} type="number" min={0}
                          value={r.asistencias || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCell(a.id, "asistencias", v === "" ? "" : (parseInt(v, 10) || 0));
                          }}
                          placeholder="—"
                          className="h-8 text-center text-xs" />
                      </td>
                      {CAMPOS.map((c) => (
                        <td key={c.key} className="p-1.5">
                          {escala === "NUMERICA" ? (
                            <Input
                              data-testid={`b-${c.key}-${a.id}`}
                              type="number" step="0.1" min={5} max={10}
                              value={r[c.key] || ""}
                              onChange={(e) => setCell(a.id, c.key, e.target.value)}
                              className="h-8 text-center text-xs font-semibold"
                              placeholder="—"
                            />
                          ) : (
                            <Select value={r[c.key] || "__none__"} onValueChange={(v) => setCell(a.id, c.key, v === "__none__" ? "" : v)}>
                              <SelectTrigger data-testid={`b-${c.key}-${a.id}`} className="h-8 text-xs">
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">—</SelectItem>
                                {NIVELES_LETRA.map((n) => (<SelectItem key={n} value={n}>{n}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                      ))}
                      <td className="p-1.5">
                        <Textarea data-testid={`b-obs-${a.id}`} rows={1}
                          value={r.observaciones || ""}
                          onChange={(e) => setCell(a.id, "observaciones", e.target.value)}
                          className="text-xs min-h-[32px] py-1.5" placeholder="—" />
                      </td>
                      <td className="p-1.5">
                        <button onClick={() => imprimirAlumno(a.id)} data-testid={`b-print-${a.id}`}
                          className="text-[#235B4E] hover:text-[#9F2241]" title="Boleta individual">
                          <Printer className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {escala === "LETRAS" && (
          <div className="mt-4 flex flex-wrap gap-3 text-[10px] text-[#6B7280]">
            {NIVELES_LETRA.map((n) => (
              <span key={n} className="inline-flex items-center gap-1">
                <span className="inline-block px-1.5 rounded bg-[#235B4E]/10 border border-[#235B4E]/40 text-center font-bold text-[#235B4E]">{n}</span>
                {NIVEL_LABEL[n]}
              </span>
            ))}
          </div>
        )}
        {escala === "NUMERICA" && (
          <div className="mt-4 text-[10px] text-[#6B7280]">
            Escala numérica del <b>5</b> al <b>10</b>. Se admiten decimales (ej. 8.5). El promedio se calcula automáticamente al imprimir la boleta.
          </div>
        )}
      </div>
    </AppLayout>
  );
}
